import { useState } from "react"
import { View, Pressable } from "react-native"
import Icon from "@react-native-vector-icons/material-design-icons"
import { Show, useObservable } from "@legendapp/state/react"
import { z } from "@white-labels/input"
import { trpc } from "@src/utils/trpc"
import { ar } from "@src/i18n/ar"
import { Button, ModalWrapper, Text, TextInput } from "@src/components/core"
import { PriceInput } from "./price-input"
import { Card, CardContent } from "@src/components/ui/card"
import { cn, priceFromDBFormat } from "@src/utils"

interface SupplierBalanceCardProps {
  supplierId: number
  balance: number
  className?: string
}

export function SupplierBalanceCard({ supplierId, balance, className }: SupplierBalanceCardProps) {
  const [showBalance, setShowBalance] = useState(false)
  const showManualBalanceModal$ = useObservable(false)

  return (
    <>
      <Card className={cn("bg-white shadow-sm border-blue-200", className)}>
        <CardContent className="p-4">
          <View className="flex-row items-center justify-between mb-2">
            <Text className="text-lg font-semibold text-gray-800">{ar.currentBalance}</Text>
            <View className="flex-row items-center gap-2">
              <Pressable onPress={() => setShowBalance(!showBalance)} className="p-2 active:opacity-70">
                <Icon name={showBalance ? "eye-off" : "eye"} size={22} color="#3B82F6" />
              </Pressable>
              <Pressable onPress={() => showManualBalanceModal$.set(true)} className="p-2 active:opacity-70">
                <Icon name="plus" size={25} color="#3B82F6" />
              </Pressable>
            </View>
          </View>

          <Text className={cn("text-3xl font-bold", balance >= 0 ? "text-green-600" : "text-red-600")}>
            {showBalance ? priceFromDBFormat(balance).toFixed(2) : "••••••"}
          </Text>
        </CardContent>
      </Card>

      <Show if={showManualBalanceModal$}>
        <ManualBalanceModal hide={() => showManualBalanceModal$.set(false)} supplierId={supplierId} />
      </Show>
    </>
  )
}

function ManualBalanceModal({ hide, supplierId }: { hide: () => void; supplierId: number }) {
  const addBalance = trpc.suppliers.addBalance.useMutation()
  const utils = trpc.useUtils()
  const [amount, setAmount] = useState<number | string>()
  const [notes, setNotes] = useState("")

  const isValid = z.safeParse(z.coerce.number().check(z.positive()), amount).success && notes.length > 0
  const isLoading = addBalance.isPending
  const disabled = !isValid || isLoading

  const onSubmit = () => {
    addBalance.mutateAsync(
      { supplierId: String(supplierId), amount: Number(amount), notes },
      {
        onSuccess() {
          utils.suppliers.get.refetch({ id: supplierId })
          hide()
        },
      },
    )
    hide()
  }

  return (
    <ModalWrapper onClose={hide}>
      <Text className="font-bold text-xl">{ar.addBalance}</Text>

      <Text className="mb-2">{ar.amount}</Text>
      <PriceInput autoFocus selectTextOnFocus className="mb-2" value={amount ? Number(amount) : undefined} onValueChange={(v) => setAmount(v)} />

      <Text className="mb-2 font-semibold text-gray-700">{ar.notes}</Text>
      <TextInput multiline numberOfLines={4} placeholder={ar.notes} value={notes} onChangeText={setNotes} className="mb-2" />

      <Button disabled={disabled} loading={isLoading} onPress={onSubmit}>
        {ar.confirm}
      </Button>
    </ModalWrapper>
  )
}
